import React, { CSSProperties, FC, ReactNode } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

import { COLORS } from 'config/constants';
import Card, { CARD_RADIUS_REM } from './Card';

const Item = styled.div<{ $active: boolean }>`
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-bottom: none;

  :first-child > {
    border-top-right-radius: ${CARD_RADIUS_REM};
    border-top-left-radius: ${CARD_RADIUS_REM};
  }

  :last-child > {
    border-bottom-right-radius: ${CARD_RADIUS_REM};
    border-bottom-left-radius: ${CARD_RADIUS_REM};
    border-bottom: 1px solid
      ${({ $active }) => ($active ? COLORS.PRIMARY : COLORS.GRAY_LIGHT)};
  }

  ${({ onClick }) =>
    onClick &&
    `
     &:hover {
       background: ${COLORS.GRAY_SUPER_LIGHT};
       cursor: pointer;
     }
   `}

  ${({ $active }) =>
    $active &&
    `
     border-color: ${COLORS.PRIMARY};
     border-bottom: 1px solid ${COLORS.PRIMARY};
   `}
`;

const Container = styled.div<{ $maxHeight: string; $scrollable?: boolean }>`
  border-radius: ${CARD_RADIUS_REM};
  background: #fff;

  .CardListItem.is-active + .CardListItem {
    border-top: none;
  }

  ${({ $maxHeight, $scrollable }) =>
    $scrollable &&
    `
    max-height: ${$maxHeight};
    overflow-y: auto;
    border-radius: ${CARD_RADIUS_REM};
    box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.05);
  `}
`;

interface CardListProps {
  children: ReactNode;
  className: string;
  maxHeight?: string;
  scrollable?: boolean;
}

/**
 * Clickable list of items within a card
 *
 * Value passed as `maxHeight` should aim to size it so that the last
 * visible item (before scrolled) is not clipped by overflow
 */

const CardList: FC<CardListProps & { style: CSSProperties }> & {
  Item: FC<CardListItemProps>;
} = ({ children, className, maxHeight = '28.5rem', scrollable, ...rest }) => (
  <Container
    className={className}
    $maxHeight={maxHeight}
    $scrollable={scrollable}
    {...rest}
  >
    {children}
  </Container>
);

interface CardListItemProps {
  children: ReactNode;
  active: boolean;
  onClick: () => void;
}

export const CardListItem: FC<CardListItemProps> = ({
  children,
  active,
  onClick,
  ...rest
}) => (
  <Item
    className={classNames('CardListItem', { 'is-active': active })}
    $active={active}
    onClick={onClick}
    {...rest}
  >
    <Card.Content>{children}</Card.Content>
  </Item>
);

CardList.Item = CardListItem;

export default CardList;
